import {
  ExperiencePointCategory,
  ExperiencePointsEvent,
  experiencePointsEvents,
} from 'fitbeat.models';
import { getConfig } from 'fitbeat.gym-core';

export type AnalyticsMemberRow = Record<string, any>;

export const analyticsCategories: Array<{ id: ExperiencePointCategory, label: string }> = [
  { id: 'exercise', label: 'Exercise and Movement' },
  { id: 'education', label: 'Education and Learning' },
  { id: 'social', label: 'Social and Challenges' },
  { id: 'achievement', label: 'Achievements' },
  { id: 'nutrition', label: 'Nutrition' },
];

const eventLabelOverrides: Partial<Record<ExperiencePointsEvent, string>> = {
  stepsCompleted: 'Steps',
  strengthWorkoutCompleted: 'Strength',
  cardioWorkoutCompleted: 'Cardio',
  balanceWorkoutCompleted: 'Balance',
  workoutCompleted: 'Workouts',
  engagedInNutrition: 'Meals',
  challengeCompletedMonthly: 'Monthly Challenges',
  challengeCompletedWeekly: 'Weekly Challenges',
  congratulationsSent: 'Sent',
  homeFeedViewed: 'App opened',
  videoWatched: 'Videos',
  articleRead: 'Articles',
  contentTopicConsumedFeatured: 'Featured',
  contentTopicConsumedInsights: 'Insights',
  contentTopicConsumedPodcast: 'Podcasts',
  contentTopicConsumedPersonalisedPool: 'Personalised',
  contentTopicConsumedNutrition: 'Nutrition',
  contentTopicConsumedFitness: 'Fitness',
  contentTopicConsumedSleep: 'Sleep',
  contentTopicConsumedMeditation: 'Meditation',
  contentTopicConsumedMentalHealth: 'Mental Health',
  contentTopicConsumedWellness: 'Insights',
  contentTopicConsumedStudio: 'Studio',
  workoutMilestoneMet: 'Milestones',
  workoutRecordBroken: 'Record',
  weeklyWorkoutGoalAchieved: 'Goals met',
  weeksActiveStreakExtended: 'Streak',
};

interface IBreakdownSlot {
  label: string;
  eventIds: ExperiencePointsEvent[];
}

const categoryBreakdownSlots: Record<ExperiencePointCategory, IBreakdownSlot[]> = {
  exercise: [
    { label: 'Strength', eventIds: ['strengthWorkoutCompleted'] },
    { label: 'Cardio', eventIds: ['cardioWorkoutCompleted'] },
    { label: 'Balance', eventIds: ['balanceWorkoutCompleted'] },
  ],
  education: [
    { label: 'Personalised', eventIds: ['contentTopicConsumedPersonalisedPool'] },
    { label: 'Featured', eventIds: ['contentTopicConsumedFeatured'] },
    { label: 'Insights', eventIds: ['contentTopicConsumedInsights', 'contentTopicConsumedWellness'] },
  ],
  social: [
    { label: 'App opened', eventIds: ['homeFeedViewed'] },
    { label: 'Sent', eventIds: ['congratulationsSent'] },
  ],
  nutrition: [
    { label: 'Meals', eventIds: ['engagedInNutrition'] },
  ],
  achievement: [
    { label: 'Goals met', eventIds: ['weeklyWorkoutGoalAchieved'] },
    { label: 'Streak', eventIds: ['weeksActiveStreakExtended'] },
    { label: 'Milestones', eventIds: ['workoutMilestoneMet'] },
    { label: 'Record', eventIds: ['workoutRecordBroken'] },
  ],
};

const categoryBreakdownConfig: Record<ExperiencePointCategory, { maxRows: number, includeOther: boolean }> = {
  exercise: { maxRows: 3, includeOther: false },
  education: { maxRows: 3, includeOther: true },
  social: { maxRows: 3, includeOther: true },
  nutrition: { maxRows: 1, includeOther: false },
  achievement: { maxRows: 4, includeOther: false },
};

interface IEventCount {
  label: string;
  eventId: ExperiencePointsEvent;
  count: number;
}

interface IBreakdownItem {
  label: string;
  count: number;
}

export const analyticsEventConfigs = experiencePointsEvents.map((eventId) => {
  const config = getConfig(eventId);
  return {
    eventId,
    category: config.category,
    label: eventLabelOverrides[eventId] || formatEventLabel(config.displayTitle),
  };
});

export interface IMembersAnalyticsOptions {
  totalMembers?: number;
  engagementBasis?: MembersAnalyticsEngagementBasis;
  studioMembersOverride?: number;
  studioWorkoutsOverride?: number;
}

export type MembersAnalyticsEngagementBasis = 'onboarded' | 'joinedTeam';

export function getEngagedUsersFootnote(basis: MembersAnalyticsEngagementBasis): string {
  const memberGroup = basis === 'joinedTeam' ? 'joined team members' : 'onboarded members';
  return `Engaged users: % of ${memberGroup} with interactions in this category during the period.`;
}

const engagedUsersTooltips: Record<ExperiencePointCategory, string> = {
  exercise: 'Counts if they recorded any steps or completed any workout in the period.',
  education:
    'Counts if they opened any content in the period (articles, podcasts, featured, insights, videos, etc.).',
  social:
    'Counts if they opened the app, sent a congratulations, or took part in a weekly or monthly ' +
    'team challenge in the period.',
  achievement:
    'Counts if they extended their weekly streak, hit their weekly goal, hit a workout milestone, ' +
    'or broke a personal record in the period.',
  nutrition: 'Counts if they opened a meal in the period. Shopping lists do not count.',
};

export function getEngagedUsersTooltip(categoryId: ExperiencePointCategory): string {
  return engagedUsersTooltips[categoryId];
}

export function parseOptionalCountInput(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0 || Math.floor(parsed) !== parsed) {
    return undefined;
  }

  return parsed;
}

export function parseTotalMembersInput(value: string): number | undefined {
  const parsed = parseOptionalCountInput(value);
  if (parsed == null || parsed <= 0) {
    return undefined;
  }

  return parsed;
}

export function buildMembersAnalytics(
  rows: AnalyticsMemberRow[],
  options: IMembersAnalyticsOptions = {},
) {
  const registeredRows = rows.filter((row) => hasValue(row.email));
  const registeredCount = registeredRows.length;
  const onboardedCount = registeredRows.filter(isOnboardedMember).length;
  const joinedTeamCount = registeredRows.filter((row) => hasTeams(row.teams)).length;
  const studioMembersCount = (
    options.studioMembersOverride ?? registeredRows.filter((row) => (
      String(row.planDescription || '').toLowerCase().includes('studio')
    )).length
  );
  const studioWorkoutsTotal = (
    options.studioWorkoutsOverride ?? sumRows(rows, 'totalCompletedStudio')
  );
  const totalMembers = options.totalMembers;
  const engagementBasis = options.engagementBasis || 'onboarded';
  const engagementDenominator = engagementBasis === 'joinedTeam' ? joinedTeamCount : onboardedCount;
  const showRegisteredPercent = (
    totalMembers != null
    && totalMembers >= registeredCount
  );
  const funnel = [
    buildFunnelItem(
      'Registered members',
      registeredCount,
      showRegisteredPercent ? totalMembers : registeredCount,
      showRegisteredPercent,
    ),
    buildFunnelItem('Onboarded members', onboardedCount, registeredCount, true),
    buildFunnelItem('Joined team', joinedTeamCount, onboardedCount, true),
  ];

  if (studioMembersCount > 0) {
    funnel.push(buildFunnelItem('Studio members', studioMembersCount, registeredCount, false));
  }

  return {
    registeredCount,
    onboardedCount,
    joinedTeamCount,
    engagementBasis,
    funnel,
    categories: analyticsCategories.map((category) => {
      const categoryEventCountFields = getCategoryEventCountFields(category.id);
      const engagedMemberCount = countEngagedRows(rows, categoryEventCountFields, engagementBasis);

      return {
        id: category.id,
        label: category.label,
        xpTotal: sumRows(rows, `xpSumUncapped-category-${category.id}`),
        engagedMemberCount,
        engagementPercent: buildPercent(engagedMemberCount, engagementDenominator),
        interactionTotal: sumFields(rows, categoryEventCountFields),
        breakdown: buildBreakdown(rows, category.id),
        engagedUsersTooltip: getEngagedUsersTooltip(category.id),
      };
    }),
    stepsTotal: sumRows(rows, 'stepsTaken'),
    studioWorkoutsTotal,
  };
}

export type MembersAnalytics = ReturnType<typeof buildMembersAnalytics>;

function buildFunnelItem(
  label: string,
  count: number,
  denominator: number,
  showPercent: boolean,
) {
  return {
    label,
    count,
    percent: buildPercent(count, denominator),
    showPercent: showPercent && denominator > 0,
  };
}

function buildPercent(count: number, total: number): number {
  return total === 0 ? 0 : Math.round((count / total) * 100);
}

function buildBreakdown(rows: AnalyticsMemberRow[], category: ExperiencePointCategory): IBreakdownItem[] {
  const eventCounts = getCategoryEventCounts(rows, category);
  const countByEventId = new Map(eventCounts.map((item) => [item.eventId, item.count]));
  const { maxRows, includeOther } = categoryBreakdownConfig[category];

  if (!includeOther) {
    const primarySlots = categoryBreakdownSlots[category].slice(0, maxRows);
    const primaryItems = buildSlotBreakdownItems(primarySlots, countByEventId);
    return sortBreakdownByCount(primaryItems);
  }

  return buildTopBreakdownWithOther(eventCounts, category, countByEventId);
}

function buildTopBreakdownWithOther(
  eventCounts: IEventCount[],
  category: ExperiencePointCategory,
  countByEventId: Map<ExperiencePointsEvent, number>,
): IBreakdownItem[] {
  const { maxRows } = categoryBreakdownConfig[category];
  const slots = categoryBreakdownSlots[category];
  const slottedEventIds = new Set(getSlotEventIds(slots));
  const items: IBreakdownItem[] = buildSlotBreakdownItems(slots, countByEventId);

  for (const eventCount of eventCounts) {
    if (!slottedEventIds.has(eventCount.eventId)) {
      items.push({ label: eventCount.label, count: eventCount.count });
    }
  }

  const rankedItems = items
    .filter((item) => item.count > 0)
    .sort((a, b) => b.count - a.count);

  if (rankedItems.length <= maxRows) {
    return rankedItems;
  }

  const topItems = rankedItems.slice(0, maxRows);
  const otherCount = rankedItems.slice(maxRows).reduce((total, item) => total + item.count, 0);

  return sortBreakdownByCount(
    otherCount > 0 ? topItems.concat({ label: 'Other', count: otherCount }) : topItems,
  );
}

function sortBreakdownByCount(items: IBreakdownItem[]): IBreakdownItem[] {
  const otherItems = items.filter((item) => item.label === 'Other');
  const rankedItems = items
    .filter((item) => item.label !== 'Other')
    .sort((a, b) => b.count - a.count);

  return rankedItems.concat(otherItems);
}

function getCategoryEventCounts(
  rows: AnalyticsMemberRow[],
  category: ExperiencePointCategory,
): IEventCount[] {
  return analyticsEventConfigs
    .filter((config) => config.category === category)
    .map((config) => ({
      label: config.label,
      eventId: config.eventId,
      count: sumRows(rows, `xpCount-event-${config.eventId}`),
    }))
    .filter((item) => item.count > 0)
    .filter((item) => category !== 'exercise' || item.eventId !== 'stepsCompleted');
}

function buildSlotBreakdownItems(
  slots: IBreakdownSlot[],
  countByEventId: Map<ExperiencePointsEvent, number>,
): IBreakdownItem[] {
  return slots
    .map((slot) => ({
      label: slot.label,
      count: sumEventCounts(slot.eventIds, countByEventId),
    }))
    .filter((item) => item.count > 0);
}

function getSlotEventIds(slots: IBreakdownSlot[]): ExperiencePointsEvent[] {
  return slots.flatMap((slot) => slot.eventIds);
}

function sumEventCounts(
  eventIds: ExperiencePointsEvent[],
  countByEventId: Map<ExperiencePointsEvent, number>,
): number {
  return eventIds.reduce((total, eventId) => total + (countByEventId.get(eventId) || 0), 0);
}

function formatEventLabel(label: string): string {
  return label.replace(/^Learned about /, '');
}

function sumRows(rows: AnalyticsMemberRow[], field: string): number {
  return rows.reduce((total, row) => total + toNumber(row[field]), 0);
}

function sumFields(rows: AnalyticsMemberRow[], fields: string[]): number {
  return fields.reduce((total, field) => total + sumRows(rows, field), 0);
}

function countEngagedRows(
  rows: AnalyticsMemberRow[],
  fields: string[],
  basis: MembersAnalyticsEngagementBasis,
): number {
  return rows.filter((row) => (
    hasValue(row.email)
    && isEngagementEligible(row, basis)
    && fields.some((field) => toNumber(row[field]) > 0)
  )).length;
}

function isEngagementEligible(row: AnalyticsMemberRow, basis: MembersAnalyticsEngagementBasis): boolean {
  return basis === 'joinedTeam' ? hasTeams(row.teams) : isOnboardedMember(row);
}

function getCategoryEventCountFields(category: ExperiencePointCategory): string[] {
  return analyticsEventConfigs
    .filter((config) => config.category === category)
    .map((config) => `xpCount-event-${config.eventId}`);
}

function isOnboardedMember(row: AnalyticsMemberRow): boolean {
  return hasValue(row.dateOfBirth);
}

function toNumber(value: any): number {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
}

function hasValue(value: any): boolean {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

function hasTeams(value: any): boolean {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return hasValue(value);
}
